import React from 'react';
import { Menu, Search, Plus, type LucideIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';

interface ActionButton {
  icon: LucideIcon;
  onClick: () => void;
  title?: string;
}

interface MobileSubMenuHeaderProps {
  onMenuClick: () => void;
  onAddClick?: () => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  showSearch?: boolean;
  showAdd?: boolean;
  isDarkMode?: boolean;
  actionButtons?: ActionButton[];
  children?: React.ReactNode;
}

export const MobileSubMenuHeader: React.FC<MobileSubMenuHeaderProps> = ({
  onMenuClick,
  onAddClick,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  showSearch = true,
  showAdd = true,
  isDarkMode = false,
  actionButtons = [],
  children,
}) => {
  return (
    <div
      className={`md:hidden fixed top-0 left-0 right-0 z-30 border-b ${
        isDarkMode ? 'bg-sidebar-accent border-sidebar-border' : 'bg-white border-gray-200'
      }`}
      style={{ height: '3.5rem' }}
    >
      <div className="flex items-center gap-2 h-full px-4">
        <button
          onClick={onMenuClick}
          className={`p-2 rounded-lg transition-colors ${
            isDarkMode
              ? 'hover:bg-sidebar text-sidebar-foreground/80'
              : 'hover:bg-gray-100 text-gray-700'
          }`}
        >
          <Menu className="w-5 h-5" />
        </button>

        {showSearch && (
          <div className="flex-1 relative">
            <Search
              className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                isDarkMode ? 'text-sidebar-foreground/50' : 'text-gray-400'
              }`}
            />
            <Input
              type="text"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearchChange?.(e.target.value)}
              className={`pl-9 pr-3 h-9 text-sm ${
                isDarkMode
                  ? 'bg-sidebar border-sidebar-border text-sidebar-foreground placeholder:text-sidebar-foreground/50'
                  : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400'
              }`}
            />
          </div>
        )}

        {!showSearch && <div className="flex-1" />}

        {/* Action buttons before the add button */}
        {actionButtons.map((button, index) => {
          const Icon = button.icon;
          return (
            <button
              key={index}
              onClick={button.onClick}
              title={button.title}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? 'hover:bg-sidebar text-sidebar-foreground/80'
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
            >
              <Icon className="w-5 h-5" />
            </button>
          );
        })}

        {children}

        {showAdd && onAddClick && (
          <button
            onClick={onAddClick}
            className="p-2 rounded-lg bg-workspace-accent text-white transition-transform active:scale-95"
          >
            <Plus className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};
